// src/components/common/ImageUploader.jsx
import React, { useState, useCallback, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { FiUploadCloud, FiX, FiFileText } from "react-icons/fi";
import { uploadMultipleImages } from "../../services/cloudinary";
import Spinner from "./Spinner";
import toast from "react-hot-toast";

export default function ImageUploader({
  folder = "products",
  onUpload,
  maxFiles = 5,
  existingImages = [],
}) {
  const [previews,  setPreviews]  = useState(existingImages);
  const [uploading, setUploading] = useState(false);
  // lightbox state
  const [lightbox,  setLightbox]  = useState(null); // url string or null

  // ── FIX ──────────────────────────────────────────────────────────────────
  // useState(existingImages) only seeds the state on the FIRST mount. If this
  // component stays mounted while the parent swaps between "Add Product" and
  // "Edit Product" (or between two different products), `previews` used to
  // keep holding the OLD product's images. Any new upload was then appended
  // to that stale list and pushed back up via onUpload(), silently wiping out
  // the real current product's images when saved.
  //
  // This effect re-syncs `previews` whenever the incoming `existingImages`
  // prop genuinely changes to a different set (i.e. parent switched products),
  // while leaving `previews` alone during normal add/remove operations within
  // the same product (those already update the ref themselves, see below).
  const lastExternalRef = useRef(JSON.stringify(existingImages));

  useEffect(() => {
    const incoming = JSON.stringify(existingImages);
    if (incoming !== lastExternalRef.current) {
      lastExternalRef.current = incoming;
      setPreviews(existingImages);
    }
  }, [existingImages]);
  // ─────────────────────────────────────────────────────────────────────────

  const isPDF = (url) =>
    typeof url === "string" && url.toLowerCase().includes(".pdf");

  // Generate a data-URL thumbnail from the first page of a PDF file
  const getPDFThumbnail = (file) =>
    new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      // Dynamically load pdf.js from CDN so we don't need a package install
      if (window.pdfjsLib) {
        renderPage(url, resolve);
      } else {
        const script = document.createElement("script");
        script.src =
          "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
        script.onload = () => {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc =
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
          renderPage(url, resolve);
        };
        script.onerror = () => resolve(null);
        document.head.appendChild(script);
      }
    });

  const renderPage = (url, resolve) => {
    window.pdfjsLib
      .getDocument(url)
      .promise.then((pdf) => pdf.getPage(1))
      .then((page) => {
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement("canvas");
        canvas.width  = viewport.width;
        canvas.height = viewport.height;
        return page
          .render({ canvasContext: canvas.getContext("2d"), viewport })
          .promise.then(() => resolve(canvas.toDataURL()));
      })
      .catch(() => resolve(null));
  };

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (previews.length + acceptedFiles.length > maxFiles) {
        toast.error(`Maximum ${maxFiles} files allowed`);
        return;
      }
      setUploading(true);
      try {
        // Generate local PDF thumbnails before uploading
        const thumbMap = {};
        await Promise.all(
          acceptedFiles.map(async (f) => {
            if (f.type === "application/pdf") {
              thumbMap[f.name] = await getPDFThumbnail(f);
            }
          })
        );

        const results = await uploadMultipleImages(acceptedFiles, folder);
        const newEntries = results.map((r, i) => ({
          url:   r.url,
          thumb: thumbMap[acceptedFiles[i].name] || null, // canvas data-url for PDFs
        }));

        // ── FIX ──────────────────────────────────────────────────────────
        // Build off the functional "prev" state (always the true, current
        // value) instead of the `previews` variable captured in this
        // callback's closure, which can be stale. Also keep the ref in sync
        // so the sync-effect above doesn't mistake this for an external
        // product switch and overwrite what we just added.
        setPreviews((prev) => {
          const newPreviews = [...prev, ...newEntries];
          // FIX: newPreviews is a MIXED array — existing images may be plain
          // strings (legacy/saved format) while newly uploaded entries are
          // {url, thumb} objects. Calling `p.url` on a string entry returns
          // undefined, which was wiping out all previously-saved images on
          // save (AdminProducts filters out non-string/undefined entries).
          // Normalise both shapes the same way `getUrl`/`remove()` already do.
          const urls = newPreviews.map((p) => (typeof p === "string" ? p : p.url));
          lastExternalRef.current = JSON.stringify(urls);
          onUpload(urls);
          return newPreviews;
        });
        // ─────────────────────────────────────────────────────────────────
      } catch {
        toast.error("Upload failed. Check Cloudinary config.");
      } finally {
        setUploading(false);
      }
    },
    [previews, folder, maxFiles, onUpload]
  );

  const remove = (idx) => {
    setPreviews((prev) => {
      const updated = prev.filter((_, i) => i !== idx);
      const urls = updated.map((p) => (typeof p === "string" ? p : p.url));
      lastExternalRef.current = JSON.stringify(urls);
      onUpload(urls);
      return updated;
    });
  };

  // Normalise: existingImages can be plain strings (legacy) or {url,thumb} objects
  const getUrl   = (item) => (typeof item === "string" ? item : item.url);
  const getThumb = (item) => (typeof item === "string" ? null : item.thumb);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [], "application/pdf": [".pdf"] },
    multiple: true,
    disabled: uploading,
  });

  return (
    <>
      <div className="space-y-3">
        {/* Drop zone */}
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all
            ${isDragActive
              ? "border-primary-500 bg-primary-50"
              : "border-gray-300 hover:border-primary-400 hover:bg-gray-50"}`}
        >
          <input {...getInputProps()} />
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Spinner />
              <p className="text-sm text-gray-500">Uploading...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <FiUploadCloud className="w-8 h-8 text-gray-400" />
              <p className="text-sm text-gray-600 font-medium">
                {isDragActive ? "Drop files here" : "Drag & drop or click to upload"}
              </p>
              <p className="text-xs text-gray-400">
                PNG, JPG, PDF, WEBP up to 10 MB · Max {maxFiles} files
              </p>
            </div>
          )}
        </div>

        {/* Previews */}
        {previews.length > 0 && (
          <div className="grid grid-cols-4 gap-2">
            {previews.map((item, idx) => {
              const url   = getUrl(item);
              const thumb = getThumb(item);
              const pdf   = isPDF(url);

              return (
                <div
                  key={idx}
                  className="relative group rounded-lg overflow-hidden border border-gray-200 aspect-square bg-gray-50 cursor-pointer"
                  onClick={() => {
                    if (pdf) window.open(url, "_blank");
                    else setLightbox(url);
                  }}
                >
                  {pdf ? (
                    thumb ? (
                      /* PDF thumbnail rendered from first page */
                      <img
                        src={thumb}
                        alt="PDF preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      /* Fallback if pdf.js failed */
                      <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-gray-400">
                        <FiFileText className="w-8 h-8 text-red-400" />
                        <span className="text-[10px] font-semibold text-red-500 uppercase tracking-wide">PDF</span>
                      </div>
                    )
                  ) : (
                    <img src={url} alt="" className="w-full h-full object-cover" />
                  )}

                  {/* PDF badge overlay */}
                  {pdf && (
                    <span className="absolute top-1 left-1 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide">
                      PDF
                    </span>
                  )}

                  {/* Remove button */}
                  <button
                    onClick={(e) => { e.stopPropagation(); remove(idx); }}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                  >
                    <FiX className="w-3 h-3" />
                  </button>

                  {/* Main badge */}
                  {idx === 0 && (
                    <span className="absolute bottom-1 left-1 bg-primary-600 text-white text-[10px] px-1.5 py-0.5 rounded">
                      Main
                    </span>
                  )}

                  {/* Click hint overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <span className="text-white text-[10px] font-medium opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 px-2 py-0.5 rounded-full">
                      {pdf ? "Open PDF" : "View"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Image Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightbox}
              alt="Preview"
              className="max-w-full max-h-[85vh] rounded-xl object-contain shadow-2xl"
            />
            <button
              onClick={() => setLightbox(null)}
              className="absolute -top-3 -right-3 bg-white text-gray-800 rounded-full p-1.5 shadow-lg hover:bg-gray-100 transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}